/**
 * Admin Orders Context - shared event selection state for all admin order tabs.
 *
 * Provides:
 * - Event list with stats (orders, tickets, pending)
 * - Selected event persistence via localStorage
 * - Type-safe state management with discriminated unions
 */

import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAdminEventsWithStats } from "../hooks";

// =============================================================================
// Types
// =============================================================================

/** Event option with stats for dropdown */
export interface EventOption {
  id: string;
  title: string;
  startsAt: string;
  imageUrl: string | null;
  venue: string | null;
  ordersCount: number;
  ticketsCount: number;
  pendingCount: number;
}

/** Context value exposed to consumers */
export interface AdminOrdersContextValue {
  /** Current state status */
  status: "loading" | "error" | "ready";
  /** Error message if status is 'error' */
  error: string | null;
  /** List of available events */
  events: EventOption[];
  /** Currently selected event ID */
  selectedEventId: string | null;
  /** Currently selected event object */
  selectedEvent: EventOption | null;
  /** Set selected event ID */
  setSelectedEventId: (id: string | null) => void;
  /** Refresh events list from server */
  refreshEvents: () => Promise<void>;
  /** Whether data is loading */
  isLoading: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = "admin_orders_selected_event";

// =============================================================================
// Context
// =============================================================================

const AdminOrdersContext = createContext<AdminOrdersContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface AdminOrdersProviderProps {
  children: ReactNode;
}

export function AdminOrdersProvider({ children }: AdminOrdersProviderProps) {
  const [selectedEventId, setSelectedEventIdState] = useState<string | null>(
    null
  );
  const [initialized, setInitialized] = useState(false);

  // URQL hook for fetching events
  const {
    events,
    isLoading,
    error: fetchError,
    refetch,
  } = useAdminEventsWithStats();

  // Initialize selection once events load
  useEffect(() => {
    if (!isLoading && events.length > 0 && !initialized) {
      const savedId = localStorage.getItem(STORAGE_KEY);
      const savedEvent = savedId ? events.find((ev) => ev.id === savedId) : null;

      const initialEventId = savedEvent ? savedEvent.id : events[0]?.id ?? null;
      setSelectedEventIdState(initialEventId);
      setInitialized(true);
    }
  }, [events, isLoading, initialized]);

  // Persist selection to localStorage
  useEffect(() => {
    if (initialized && selectedEventId) {
      localStorage.setItem(STORAGE_KEY, selectedEventId);
    }
  }, [selectedEventId, initialized]);

  // Set selected event
  const setSelectedEventId = useCallback((eventId: string | null) => {
    setSelectedEventIdState(eventId);
  }, []);

  // Refresh events
  const refreshEvents = useCallback(async () => {
    refetch();
  }, [refetch]);

  // Memoized context value
  const value = useMemo<AdminOrdersContextValue>(() => {
    const status = isLoading
      ? "loading"
      : fetchError
        ? "error"
        : "ready";

    const selectedEvent =
      events.find((ev) => ev.id === selectedEventId) ?? null;

    return {
      status,
      error: fetchError?.message ?? null,
      events,
      selectedEventId,
      selectedEvent,
      setSelectedEventId,
      refreshEvents,
      isLoading,
    };
  }, [
    events,
    isLoading,
    fetchError,
    selectedEventId,
    setSelectedEventId,
    refreshEvents,
  ]);

  return (
    <AdminOrdersContext.Provider value={value}>
      {children}
    </AdminOrdersContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access admin orders context.
 * Must be used within AdminOrdersProvider.
 */
export function useAdminOrdersContext(): AdminOrdersContextValue {
  const context = useContext(AdminOrdersContext);

  if (!context) {
    throw new Error(
      "useAdminOrdersContext must be used within AdminOrdersProvider",
    );
  }

  return context;
}

/**
 * Hook to access selected event from context.
 * Returns null if no event is selected or context is not ready.
 */
export function useSelectedEvent(): EventOption | null {
  const { selectedEvent } = useAdminOrdersContext();
  return selectedEvent;
}
